import {
  clearApiCallHistory,
  flushApiCallHistoryPersistence,
} from 'src/utils/http/api-call-history';

import useUserDataStore from 'src/store/user';
import { STORE_REGISTRY } from 'src/store/store-registry';
import { invalidateSessionWork } from 'src/auth/session-lifecycle';
import { resetOperatorAuthenticationState } from 'src/store/OperatorAuthentication';
import { AUTH_RECOVERY_LOOP_KEY } from 'src/auth/store/firebase/auth-recovery-loop';
import { resetTapToPaySetupSession } from 'src/manager/pay/services/tap-to-pay-setup-store';
import {
  type AuthLogoutReason,
  shouldPreservePairingForLogout,
} from 'src/auth/store/firebase/logout-reason';

import { clearLocalDataForLogout } from './DB_Utils';
import { resetDeviceIdentity } from './auth-v3/device-identity';
import { drainInventoryWrites } from './inventory-write-barrier';
import { invalidateDeviceSessionCache } from './auth-v3/session-store';
import { drainManagerCacheWrites } from './manager-cache-write-barrier';
import { suspendActiveSalePersistenceForLogout } from './active-sale-persistence';
import { invalidateRegisteredStripeM2Session } from './stripe-m2-session-lifecycle';
import { SECURE_STORAGE_KEYS, secureStorageService } from './secure-storage.service';

/**
 * localStorage keys that must survive the logout wipe below. These hold only
 * non-privileged lookup hints (never tokens) that power a faster next sign-in.
 * - manager-saved-login-profile: the Manager app's saved-account roster used to
 *   offer one-tap "sign back in as <name>" cards after logout.
 */
const LOGOUT_PRESERVE_KEYS = [
  'manager-saved-login-profile',
  'pointpay-saved-login-profiles',
  AUTH_RECOVERY_LOOP_KEY,
];

export interface LogoutCleanupOptions {
  reason: AuthLogoutReason;
}

function getPreservedLocalStorageKeys(preserveDeviceReauthentication: boolean): string[] {
  if (!preserveDeviceReauthentication) return LOGOUT_PRESERVE_KEYS;

  const deviceSessionKey = SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3;
  const pendingDeviceSessionKey = SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING;
  const secureStoragePrefix = secureStorageService.getKeyPrefix();

  return [
    ...LOGOUT_PRESERVE_KEYS,
    deviceSessionKey,
    `${secureStoragePrefix}${deviceSessionKey}`,
    pendingDeviceSessionKey,
    `${secureStoragePrefix}${pendingDeviceSessionKey}`,
  ];
}

/**
 * Remove user, tenant, device-pairing, and cached application data. The admin
 * debug re-authentication test can retain the minimum paired-device material
 * needed to exercise session refresh after logout.
 *
 * The local database cleanup retains the force-logout acknowledgement plus
 * non-sensitive Manager Tap to Pay guide history. Live account-link, reader,
 * provider credentials, and all tenant data are still cleared.
 */
export async function clearApplicationDataForLogout(options: LogoutCleanupOptions): Promise<void> {
  invalidateSessionWork();
  const preserveDeviceReauthentication = shouldPreservePairingForLogout(options.reason);

  // Invalidate memory-only provider credentials and reader ownership before
  // any persisted tenant identity is cleared. Late native callbacks then fail
  // their generation/scope check instead of reaching the next signed-in user.
  await invalidateRegisteredStripeM2Session('logout', {
    disconnect: true,
    clearCredentialsIfSafe: true,
  });

  // Stop and drain the active-sale writer before clearing the database. Without
  // this boundary, a pending cart write could recreate tenant data after wipe.
  await suspendActiveSalePersistenceForLogout();
  await drainInventoryWrites();
  await drainManagerCacheWrites();
  await clearLocalDataForLogout();

  const companionStore = STORE_REGISTRY.companion?.hook;
  companionStore?.getState().stopListening?.();

  Object.values(STORE_REGISTRY).forEach(({ hook }) => {
    hook.setState(hook.getInitialState(), true);
  });
  useUserDataStore.setState(useUserDataStore.getInitialState(), true);
  clearApiCallHistory();
  await flushApiCallHistoryPersistence();
  resetOperatorAuthenticationState();
  resetTapToPaySetupSession();

  const secureKeysToRemove = Object.values(SECURE_STORAGE_KEYS).filter(
    (key) =>
      !preserveDeviceReauthentication ||
      (key !== SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3 &&
        key !== SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING)
  );
  const cleanupTasks = secureKeysToRemove.map((key) => secureStorageService.remove(key));
  if (!preserveDeviceReauthentication) cleanupTasks.push(resetDeviceIdentity());

  // The in-memory last-known-good device session must never outlive a logout
  // wipe. Invalidate unconditionally: pairing-preserving logouts keep the
  // stored keys, so the next read simply re-populates the cache.
  invalidateDeviceSessionCache();
  await Promise.all(cleanupTasks);

  // Persist middleware may write empty initial states while the in-memory stores
  // reset above, so browser storage is cleared last.
  if (typeof localStorage !== 'undefined') {
    // Delete individually: financial evidence must never disappear, even transiently.
    const preserved = new Set(getPreservedLocalStorageKeys(preserveDeviceReauthentication));
    Object.keys(localStorage).forEach((key) => {
      const financial =
        key === 'pointcraft-sale-v4' ||
        key === 'pointcraft-sale-v5' ||
        key.startsWith('pointcraft-payment-') ||
        key.startsWith('pointcraft.payment-original:') ||
        key.startsWith('pointcraft.v4.completed-report.') ||
        key.startsWith('pointcraft.v5.finalization:') ||
        key.startsWith('pointcraft-manager-payment-') ||
        key.startsWith('manager_pay_business_location:') ||
        key.startsWith('pointcraft.pos.activeSale.v1:') ||
        key.startsWith('manager-payment-terminal:') ||
        key.startsWith('pointcraft-active-sale') ||
        key.startsWith('sale-order-details');
      if (!preserved.has(key) && !financial) localStorage.removeItem(key);
    });
  }
  if (typeof sessionStorage !== 'undefined') sessionStorage.clear();
}
